import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setAppLocale, type AppLocale } from '../i18n/config';

const OPTIONS: { value: AppLocale; labelKey: 'langVi' | 'langEn' | 'langRu'; flag: string }[] = [
  { value: 'vi', labelKey: 'langVi', flag: '🇻🇳' },
  { value: 'en', labelKey: 'langEn', flag: '🇬🇧' },
  { value: 'ru', labelKey: 'langRu', flag: '🇷🇺' },
];

type Variant = 'login' | 'settings';

export default function LanguageSwitcher({ variant = 'settings' }: { variant?: Variant }) {
  const { t, i18n } = useTranslation();
  const current = (i18n.language?.split('-')[0] as AppLocale) || 'vi';
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerId = useId();

  const currentOpt = OPTIONS.find((o) => o.value === current) ?? OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const isLogin = variant === 'login';

  const labelClass = isLogin ? 'text-xs text-white/70' : 'text-xs text-gray-500 dark:text-gray-400';

  const triggerClass = isLogin
    ? 'w-full max-w-[min(100%,280px)] rounded-xl border border-white/25 bg-white/10 px-3 py-2.5 text-sm text-white shadow-sm backdrop-blur-sm transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-blue-400/80'
    : 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm transition hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900/50 dark:text-gray-100 dark:hover:border-gray-500';

  const menuClass = isLogin
    ? 'absolute left-1/2 z-[80] mt-1 w-full max-w-[min(100%,280px)] -translate-x-1/2 rounded-xl border border-white/20 bg-slate-900/95 py-1 shadow-xl backdrop-blur-md'
    : 'absolute left-0 z-[80] mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-900';

  const itemClass = (active: boolean) =>
    isLogin
      ? `flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition ${
          active ? 'bg-white/15 text-white' : 'text-white/90 hover:bg-white/10'
        }`
      : `flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition ${
          active ? 'bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100' : 'text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800/80'
        }`;

  return (
    <div ref={rootRef} className={`relative flex flex-col gap-1.5 ${isLogin ? 'items-center' : ''}`}>
      <span className={labelClass}>{t('common.language')}</span>
      <div className={isLogin ? 'relative w-full max-w-[min(100%,280px)]' : 'relative w-full'}>
        <button
          type="button"
          id={triggerId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('common.language')}
          onClick={() => setOpen((v) => !v)}
          className={`flex w-full items-center justify-between gap-2 ${triggerClass}`}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="text-lg leading-none" aria-hidden>
              {currentOpt.flag}
            </span>
            <span className="truncate font-medium">{t(`common.${currentOpt.labelKey}`)}</span>
          </span>
          <svg
            className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.512a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {open && (
          <ul
            role="listbox"
            aria-labelledby={triggerId}
            className={menuClass}
          >
            {OPTIONS.map((o) => {
              const active = o.value === current;
              return (
                <li key={o.value} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={itemClass(active)}
                    onClick={() => {
                      setAppLocale(o.value);
                      setOpen(false);
                    }}
                  >
                    <span className="text-lg leading-none" aria-hidden>
                      {o.flag}
                    </span>
                    <span className="font-medium">{t(`common.${o.labelKey}`)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
